/* @flow */

import * as BundleSelectionUtil from "./BundleSelectionUtil";
import * as SearchUtil from "./SearchUtil";
import { includeMarginTaxesInPrice, primaryIsGross } from "./PriceDisplayFramework";

import { PriceConfig } from "shared/src/types/articlesearch/Price";
import { Pagination, SearchResultEx, SearchResultItem } from "shared/src/types/articlesearch/SearchResult";

export const parseSortingString = (sorting: string): [string, boolean] => {
    const cleanString = (sorting || "").replace(/\s\s+/g, " ").trim().toLowerCase();
    const [mode, order] = cleanString.split(" ", 2);
    return [mode, order === "asc"];
};

export const extractPriceSortingKeys = (
    item: SearchResultItem,
    storeId: string,
    ascending: boolean,
    priceConfig: PriceConfig
): number => {
    let price;
    if (item.state === "loaded") {
        const variant = item.successor ? item.successor : item.variant;
        const bundle = variant ? BundleSelectionUtil.getBundleToShow(variant.bundles, storeId) : null;
        const store = bundle?.stores?.[storeId];
        const useGross = priceConfig && primaryIsGross(priceConfig);
        const includeTaxes = priceConfig && includeMarginTaxesInPrice(priceConfig);
        if (useGross && includeTaxes) {
            price = store?.sellingPriceInfo?.finalPricesInfo?.sumGross;
        }
        if (useGross && !includeTaxes) {
            price = store?.sellingPriceInfo?.finalPricesInfo?.articleGross;
        }
        if (!useGross && includeTaxes) {
            price = store?.sellingPriceInfo?.finalPricesInfo?.sumNet;
        }
        if (!useGross && !includeTaxes) {
            price = store?.sellingPriceInfo?.finalPricesInfo?.articleNet;
        }
    }
    return price ?? (ascending ? 999999999 : 0);
};

export const sortSearchItems = (
    items: Array<SearchResultItem>,
    storeId: string,
    mode: string,
    ascending: boolean,
    priceConfig: PriceConfig
): Array<SearchResultItem> => {
    if (mode === "price") {
        const sortKeyMap = new Map<SearchResultItem, number>();
        for (const item of items) {
            sortKeyMap.set(item, extractPriceSortingKeys(item, storeId, ascending, priceConfig));
        }
        return [...items].sort((a, b) => {
            const keyA = sortKeyMap.get(a) ?? 0;
            const keyB = sortKeyMap.get(b) ?? 0;
            if (keyA === keyB) {
                return String(a.id ?? "").localeCompare(String(b.id ?? ""));
            } else {
                return ascending ? keyA - keyB : keyB - keyA;
            }
        });
    } else {
        return items;
    }
};

export const extractSortedPrefix = (
    currentSearchResult: Array<SearchResultItem>,
    oldSearchResult: Array<SearchResultItem>,
    pagination: Pagination
): [Array<SearchResultItem>, Array<SearchResultItem>] => {
    const head = (oldSearchResult ?? []).slice(0, (pagination.page - 1) * pagination.rows);
    const tail = (currentSearchResult ?? []).filter((item) => !head.some((headItem) => headItem.id === item.id));
    return [head, tail];
};

export const mergeSearchResultItems = (
    head: ReadonlyArray<SearchResultItem>,
    tail: ReadonlyArray<SearchResultItem>,
    pagination: Pagination
): Array<SearchResultItem> => {
    return [...head, ...tail].slice(0, pagination.page * pagination.rows);
};

// sorting merging and splitting search results destroys internal logic. This reconstructs the structure.
export const reconstructSearchResult = (result: SearchResultEx): SearchResultEx => {
    (result.result ?? []).forEach((item: SearchResultItem, index: number) => {
        item.position = index;
    });
    result.resultIds = (result.result ?? []).map((item) => item.id);
    result.totalPages = Math.ceil((result.amount || 0) / (result.rows || 1));
    result.idSearchPosition = SearchUtil.mapToCountedIds(result.resultIds);
    return result;
};
